import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { API } from "@/App";
import { toast } from "sonner";
import BottomNav from "@/components/BottomNav";
import {
  Plus, Target, MapPin, Calendar, Trash2, DollarSign,
  Users, ChevronRight, PiggyBank, Sparkles, Crown,
  Mail, Check, X, UserPlus, LogOut, Award, Share2, Edit2, PartyPopper
} from "lucide-react";

export default function Goals() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [goals, setGoals] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showContributeModal, setShowContributeModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationGoal, setCelebrationGoal] = useState(null);
  const [editGoal, setEditGoal] = useState({ title: "", target_amount: "", destination: "" });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: "", message: "", onConfirm: null });
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [goalMembers, setGoalMembers] = useState([]);
  const [newGoal, setNewGoal] = useState({
    title: "",
    destination: "",
    target_amount: "",
    goal_type: "solo"
  });
  const [contributeAmount, setContributeAmount] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [isInviting, setIsInviting] = useState(false);

  const showConfirm = (title, message, onConfirm) => {
    setConfirmDialog({ open: true, title, message, onConfirm });
  };
  
  useEffect(() => {
    fetchData();
  }, []);
  
  useEffect(() => {
    // Check for invitation in URL
    const invitationId = searchParams.get("invitation");
    if (invitationId && invitations.length > 0) {
      const inv = invitations.find(i => i.invitation_id === invitationId);
      if (inv) {
        toast.info(`You have a pending invitation from ${inv.inviter?.name}`);
      }
    }
  }, [searchParams, invitations]);
  
  const fetchData = async () => {
    try {
      const [userRes, goalsRes, invRes] = await Promise.all([
        fetch(`${API}/auth/me`, { credentials: "include" }),
        fetch(`${API}/goals/`, { credentials: "include" }),
        fetch(`${API}/goals/invitations`, { credentials: "include" })
      ]);
      
      if (userRes.ok) setUser(await userRes.json());
      if (goalsRes.ok) setGoals(await goalsRes.json());
      if (invRes.ok) setInvitations(await invRes.json());
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to load goals. Please refresh.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateGoal = async () => {
    if (!newGoal.title || !newGoal.target_amount) {
      toast.error("Please fill in all required fields");
      return;
    }
    const amount = parseFloat(newGoal.target_amount);
    if (isNaN(amount) || amount < 1) {
      toast.error("Target amount must be at least $1");
      return;
    }
    if (amount > 1_000_000) {
      toast.error("Target amount cannot exceed $1,000,000");
      return;
    }
    
    try {
      const response = await fetch(`${API}/goals/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...newGoal,
          target_amount: parseFloat(newGoal.target_amount)
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || "Failed to create goal");
      }
      
      toast.success(newGoal.goal_type === "group" ? "Group goal created! Invite friends to join! 🎯" : "Goal created! Start saving! 🎯");
      setShowCreateModal(false);
      setNewGoal({ title: "", destination: "", target_amount: "", goal_type: "solo" });
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };
  
  const handleContribute = async () => {
    if (!contributeAmount || parseFloat(contributeAmount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    
    try {
      const response = await fetch(`${API}/goals/${selectedGoal.goal_id}/contribute`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ amount: parseFloat(contributeAmount) })
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.detail || "Failed to add contribution");
      }
      
      toast.success(`$${contributeAmount} added to your goal! 🐷`);

      if (result.status === "completed") {
        setCelebrationGoal(selectedGoal);
        setShowCelebration(true);
      }
      
      setShowContributeModal(false);
      setContributeAmount("");
      setSelectedGoal(null);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };
  
  const handleDeleteGoal = (goalId) => {
    showConfirm(
      "Delete Goal",
      "Are you sure? Any saved amount will be returned to your wallet.",
      async () => {
        try {
          const response = await fetch(`${API}/goals/${goalId}`, {
            method: "DELETE",
            credentials: "include"
          });
          if (!response.ok) throw new Error("Failed to delete goal");
          const result = await response.json();
          toast.success(`Goal deleted. $${result.refunded.toFixed(2)} returned to wallet.`);
          fetchData();
        } catch (error) {
          toast.error(error.message);
        }
      }
    );
  };

  
  const handleOpenEdit = (goal) => {
    setSelectedGoal(goal);
    setEditGoal({ title: goal.title, target_amount: goal.target_amount.toString(), destination: goal.destination || "" });
    setShowEditModal(true);
  };

  const handleEditGoal = async () => {
    const amount = parseFloat(editGoal.target_amount);
    if (!editGoal.title.trim()) { toast.error("Title cannot be empty"); return; }
    if (isNaN(amount) || amount < 1) { toast.error("Target amount must be at least $1"); return; }
    if (amount > 1_000_000) { toast.error("Target amount cannot exceed $1,000,000"); return; }
    try {
      const response = await fetch(`${API}/goals/${selectedGoal.goal_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ title: editGoal.title.trim(), target_amount: amount, destination: editGoal.destination })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Failed to update goal");
      toast.success("Goal updated!");
      setShowEditModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleInviteMember = async () => {
    if (!inviteEmail) {
      toast.error("Please enter an email address");
      return;
    }
    
    setIsInviting(true);
    try {
      const response = await fetch(`${API}/goals/${selectedGoal.goal_id}/invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: inviteEmail })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || "Failed to send invitation");
      }
      
      toast.success(`Invitation sent to ${inviteEmail}! 📧`);
      setInviteEmail("");
      setShowInviteModal(false);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsInviting(false);
    }
  };
  
  const handleAcceptInvitation = async (invitationId) => {
    try {
      const response = await fetch(`${API}/goals/invitations/${invitationId}/accept`, {
        method: "POST",
        credentials: "include"
      });
      
      if (!response.ok) throw new Error("Failed to accept invitation");
      
      toast.success("You've joined the group goal! 🎉");
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };
  
  const handleDeclineInvitation = async (invitationId) => {
    try {
      const response = await fetch(`${API}/goals/invitations/${invitationId}/decline`, {
        method: "POST",
        credentials: "include"
      });
      
      if (!response.ok) throw new Error("Failed to decline invitation");
      
      toast.info("Invitation declined");
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };
  
  const handleViewMembers = async (goal) => {
    setSelectedGoal(goal);
    try {
      const response = await fetch(`${API}/goals/${goal.goal_id}/members`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setGoalMembers(data.members);
        setShowMembersModal(true);
      }
    } catch (error) {
      toast.error("Failed to load members");
    }
  };
  
  const handleLeaveGoal = (goalId) => {
    showConfirm(
      "Leave Group Goal",
      "Are you sure you want to leave this group goal?",
      async () => {
        try {
          const response = await fetch(`${API}/goals/${goalId}/leave`, {
            method: "POST",
            credentials: "include"
          });
          if (!response.ok) throw new Error("Failed to leave goal");
          toast.info("You've left the group goal");
          setShowMembersModal(false);
          fetchData();
        } catch (error) {
          toast.error(error.message);
        }
      }
    );
  };
  
  const activeGoals = goals.filter(g => g.status === "active");
  const completedGoals = goals.filter(g => g.status === "completed");
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 safe-bottom" data-testid="goals-skeleton">
        {/* Skeleton Header */}
        <div className="bg-white border-b border-slate-100 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="h-6 w-36 bg-slate-200 rounded animate-pulse mb-1" />
              <div className="h-3 w-20 bg-slate-100 rounded animate-pulse" />
            </div>
            <div className="h-10 w-24 bg-slate-200 rounded-full animate-pulse" />
          </div>
        </div>
        <div className="px-6 py-6 space-y-4">
          {/* Skeleton invitation banner */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-200 animate-pulse" />
              <div className="flex-1">
                <div className="h-4 w-40 bg-amber-200 rounded animate-pulse mb-2" />
                <div className="h-3 w-56 bg-amber-100 rounded animate-pulse" />
              </div>
            </div>
          </div>
          {/* Skeleton goal cards */}
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white rounded-2xl p-5 shadow-sm">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-14 h-14 rounded-2xl bg-slate-200 animate-pulse" />
                <div className="flex-1">
                  <div className="h-5 w-3/4 bg-slate-200 rounded animate-pulse mb-2" />
                  <div className="h-3 w-1/2 bg-slate-100 rounded animate-pulse" />
                </div>
              </div>
              <div className="h-2 w-full bg-slate-100 rounded-full animate-pulse mb-3" />
              <div className="flex justify-between">
                <div className="h-3 w-16 bg-slate-100 rounded animate-pulse" />
                <div className="h-3 w-16 bg-slate-100 rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>
        <BottomNav />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-50 safe-bottom" data-testid="goals-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 px-6 py-4 sticky top-0 z-40">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Savings Goals</h1>
            <p className="text-sm text-slate-500">{activeGoals.length} active goals</p>
          </div>
          <Button 
            onClick={() => setShowCreateModal(true)}
            className="savecation-button h-10 px-4"
            data-testid="create-goal-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> New Goal
          </Button>
        </div>
      </header>
      
      <main className="px-6 py-6">
        {/* Pending Invitations Banner */}
        {invitations.length > 0 && (
          <div className="mb-6 space-y-3">
            <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-2">
              <Mail className="w-4 h-4" /> Pending Invitations ({invitations.length})
            </h2>
            {invitations.map(inv => (
              <div key={inv.invitation_id} className="bg-gradient-to-r from-amber-50 to-amber-100/50 border border-amber-200 rounded-2xl p-4">
                <div className="flex items-center gap-3 mb-3">
                  <Avatar className="w-10 h-10">
                    <AvatarImage src={inv.inviter?.picture} />
                    <AvatarFallback className="bg-amber-200 text-amber-700">
                      {inv.inviter?.name?.charAt(0) || "?"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <p className="font-semibold text-slate-900">
                      {inv.inviter?.name} invited you
                    </p>
                      <p className="text-sm text-slate-500">
                      Goal: {inv.goal?.title} • ${inv.goal?.target_amount?.toLocaleString()}
                    </p>
                    {inv.created_at && (
                      <p className="text-xs text-amber-600">
                        Expires {new Date(new Date(inv.created_at).getTime() + 7*24*60*60*1000).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleAcceptInvitation(inv.invitation_id)}
                    className="flex-1 bg-emerald-500 hover:bg-emerald-600 rounded-full"
                    size="sm"
                    data-testid={`accept-${inv.invitation_id}`}
                  >
                    <Check className="w-4 h-4 mr-1" /> Accept
                  </Button>
                  <Button
                    onClick={() => handleDeclineInvitation(inv.invitation_id)}
                    variant="outline"
                    className="flex-1 rounded-full"
                    size="sm"
                    data-testid={`decline-${inv.invitation_id}`}
                  >
                    <X className="w-4 h-4 mr-1" /> Decline
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Pro Upgrade Banner (for free users) */}
        {user?.subscription_tier !== "pro" && (
          <div 
            className="bg-gradient-to-r from-amber-500 to-amber-600 rounded-2xl p-4 mb-6 cursor-pointer"
            onClick={() => navigate("/settings")}
          >
            <div className="flex items-center gap-3 text-white">
              <Crown className="w-8 h-8" />
              <div className="flex-1">
                <p className="font-bold">Unlock Group Goals!</p>
                <p className="text-sm text-amber-100">Upgrade to Pro to save together with friends & family</p>
              </div>
              <ChevronRight className="w-5 h-5 text-amber-200" />
            </div>
          </div>
        )}
        
        {/* Wallet Balance Quick View */}
        <div 
          className="bg-sky-500 rounded-2xl p-4 mb-6 flex items-center justify-between cursor-pointer"
          onClick={() => navigate("/wallet")}
        >
          <div className="flex items-center gap-3 text-white">
            <PiggyBank className="w-8 h-8" />
            <div>
              <p className="text-sm text-sky-100">Available to Save</p>
              <p className="text-xl font-bold">${user?.wallet_balance?.toFixed(2) || "0.00"}</p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-sky-200" />
        </div>
        
        {/* Active Goals */}
        {activeGoals.length === 0 && completedGoals.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-24 h-24 rounded-full bg-sky-100 flex items-center justify-center mx-auto mb-6">
              <Target className="w-12 h-12 text-sky-500" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">No goals yet!</h2>
            <p className="text-slate-500 mb-6">Create your first savings goal and start your journey!</p>
            <Button 
              onClick={() => setShowCreateModal(true)}
              className="savecation-button"
              data-testid="create-first-goal"
            >
              <Plus className="w-4 h-4 mr-2" /> Create Your First Goal
            </Button>
          </div>
        ) : (
          <>
            {activeGoals.length > 0 && (
              <section className="mb-8">
                <h2 className="text-lg font-bold text-slate-900 mb-4">Active Goals</h2>
                <div className="space-y-4">
                  {activeGoals.map(goal => {
                    const progress = (goal.current_amount / goal.target_amount) * 100;
                    const remaining = goal.target_amount - goal.current_amount;
                    
                    return (
                      <div key={goal.goal_id} className="goal-card" data-testid={`goal-${goal.goal_id}`}>
                        {goal.image_url && (
                          <img src={goal.image_url} alt={goal.title} className="goal-card-image" />
                        )}
                        <div className="goal-card-content">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-bold text-slate-900">{goal.title}</h3>
                                {goal.goal_type === "group" && (
                                  <span className="badge-pro text-xs py-0.5">
                                    <Users className="w-3 h-3 mr-1" /> Group
                                  </span>
                                )}
                              </div>
                              {goal.destination && (
                                <p className="text-sm text-slate-500 flex items-center gap-1">
                                  <MapPin className="w-3 h-3" /> {goal.destination}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              {goal.goal_type === "group" && (
                                <button
                                  onClick={() => handleViewMembers(goal)}
                                  className="p-2 text-slate-400 hover:text-sky-500 rounded-lg hover:bg-sky-50"
                                  data-testid={`members-${goal.goal_id}`}
                                  title="View members"
                                >
                                  <Users className="w-4 h-4" />
                                </button>
                              )}
                              {goal.user_id === user?.user_id && (
                                <button
                                  onClick={() => handleOpenEdit(goal)}
                                  className="p-2 text-slate-400 hover:text-sky-500 rounded-lg hover:bg-sky-50"
                                  data-testid={`edit-goal-${goal.goal_id}`}
                                  title="Edit goal"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                              )}
                              {goal.user_id === user?.user_id && (
                                <button
                                  onClick={() => handleDeleteGoal(goal.goal_id)}
                                  className="p-2 text-slate-400 hover:text-rose-500 rounded-lg hover:bg-rose-50"
                                  data-testid={`delete-goal-${goal.goal_id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>
                          
                          <div className="mb-3">
                            <div className="flex justify-between text-sm mb-1">
                              <span className="font-semibold text-sky-600">${goal.current_amount.toFixed(0)}</span>
                              <span className="text-slate-500">${goal.target_amount.toFixed(0)}</span>
                            </div>
                            <Progress value={progress} className="h-3 rounded-full" />
                          </div>
                          
                          {/* Group members preview */}
                          {goal.goal_type === "group" && goal.group_members?.length > 0 && (
                            <div className="flex items-center gap-2 mb-3">
                              <div className="flex -space-x-2">
                                {goal.group_members.slice(0, 3).map((memberId, i) => (
                                  <div key={memberId} className="w-6 h-6 rounded-full bg-slate-200 border-2 border-white flex items-center justify-center">
                                    <span className="text-xs text-slate-600">{i + 1}</span>
                                  </div>
                                ))}
                              </div>
                              <span className="text-xs text-slate-500">
                                +{goal.group_members.length + 1} members
                              </span>
                            </div>
                          )}
                          
                          <div className="flex items-center justify-between">
                            <p className="text-sm text-slate-500">
                              ${remaining.toFixed(0)} to go
                            </p>
                            <div className="flex gap-2">
                              {goal.goal_type === "group" && goal.user_id === user?.user_id && (
                                <Button 
                                  size="sm"
                                  variant="outline"
                                  onClick={() => { setSelectedGoal(goal); setShowInviteModal(true); }}
                                  className="rounded-full"
                                  data-testid={`invite-${goal.goal_id}`}
                                >
                                  <UserPlus className="w-4 h-4" />
                                </Button>
                              )}
                              <Button 
                                size="sm"
                                onClick={() => { setSelectedGoal(goal); setShowContributeModal(true); }}
                                className="bg-sky-500 hover:bg-sky-600 rounded-full font-bold"
                                data-testid={`contribute-${goal.goal_id}`}
                              >
                                <DollarSign className="w-4 h-4 mr-1" /> Add Money
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
            
            {completedGoals.length > 0 && (
              <section className="mb-24">
                <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-500" /> Completed Goals
                </h2>
                <div className="space-y-4">
                  {completedGoals.map(goal => (
                    <div key={goal.goal_id} className="savecation-card bg-gradient-to-r from-emerald-50 to-emerald-100/50 border-emerald-200">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-emerald-200 flex items-center justify-center">
                          <Target className="w-6 h-6 text-emerald-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-bold text-slate-900">{goal.title}</h3>
                          <p className="text-sm text-emerald-600 font-medium">
                            Goal reached! ${goal.target_amount.toFixed(0)}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="rounded-full"
                          data-testid={`share-goal-${goal.goal_id}`}
                          onClick={async () => {
                            try {
                              const res = await fetch(`${API}/gamification/share/goal/${goal.goal_id}`, { credentials: "include" });
                              if (res.ok) {
                                const data = await res.json();
                                if (navigator.share) {
                                  navigator.share({ title: data.title, text: data.text, url: data.url });
                                } else {
                                  navigator.clipboard.writeText(`${data.text} ${data.url}`);
                                  toast.success("Copied to clipboard!");
                                }
                              }
                            } catch (e) {
                              toast.error("Could not share");
                            }
                          }}
                        >
                          <Share2 className="w-5 h-5 text-emerald-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </main>
      
      {/* Create Goal Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Create New Goal</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Goal Name *</label>
              <Input 
                placeholder="e.g., Paris Trip 2025"
                value={newGoal.title}
                onChange={(e) => setNewGoal({ ...newGoal, title: e.target.value })}
                className="savecation-input"
                data-testid="goal-title-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Destination (optional)</label>
              <Input 
                placeholder="e.g., Paris, France"
                value={newGoal.destination}
                onChange={(e) => setNewGoal({ ...newGoal, destination: e.target.value })}
                className="savecation-input"
                data-testid="goal-destination-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Target Amount *</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                <Input 
                  type="number"
                  placeholder="3000"
                  value={newGoal.target_amount}
                  onChange={(e) => setNewGoal({ ...newGoal, target_amount: e.target.value })}
                  className="savecation-input pl-8"
                  data-testid="goal-amount-input"
                />
              </div>
            </div>
            {user?.subscription_tier === "pro" && (
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Goal Type</label>
                <div className="flex gap-2">
                  <Button 
                    type="button"
                    variant={newGoal.goal_type === "solo" ? "default" : "outline"}
                    onClick={() => setNewGoal({ ...newGoal, goal_type: "solo" })}
                    className="flex-1 rounded-full"
                  >
                    Solo
                  </Button>
                  <Button 
                    type="button"
                    variant={newGoal.goal_type === "group" ? "default" : "outline"}
                    onClick={() => setNewGoal({ ...newGoal, goal_type: "group" })}
                    className="flex-1 rounded-full"
                  >
                    <Users className="w-4 h-4 mr-2" /> Group
                  </Button>
                </div>
              </div>
            )}
            <Button 
              onClick={handleCreateGoal}
              className="savecation-button w-full"
              data-testid="submit-goal-btn"
            >
              Create Goal
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Contribute Modal */}
      <Dialog open={showContributeModal} onOpenChange={setShowContributeModal}>
        <DialogContent className="sm:max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Add to {selectedGoal?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="text-center py-4 bg-slate-50 rounded-2xl">
              <p className="text-sm text-slate-500">Available in Wallet</p>
              <p className="text-3xl font-bold text-sky-600">${user?.wallet_balance?.toFixed(2)}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Amount to Add</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                <Input 
                  type="number"
                  placeholder="100"
                  value={contributeAmount}
                  onChange={(e) => setContributeAmount(e.target.value)}
                  className="savecation-input pl-8"
                  data-testid="contribute-amount-input"
                />
              </div>
            </div>
            <div className="flex gap-2">
              {[25, 50, 100].map(amount => (
                <Button 
                  key={amount}
                  variant="outline"
                  onClick={() => setContributeAmount(amount.toString())}
                  className="flex-1 rounded-full"
                >
                  ${amount}
                </Button>
              ))}
            </div>
            <Button 
              onClick={handleContribute}
              disabled={!contributeAmount || parseFloat(contributeAmount) > (user?.wallet_balance || 0)}
              className="savecation-button w-full"
              data-testid="submit-contribute-btn"
            >
              Add ${contributeAmount || "0"} to Goal
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Invite Member Modal */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent className="sm:max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-sky-500" /> Invite to {selectedGoal?.title}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-slate-500 text-sm">
              Invite friends or family to contribute together towards your savings goal!
            </p>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Email Address</label>
              <Input 
                type="email"
                placeholder="friend@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="savecation-input"
                data-testid="invite-email-input"
              />
            </div>
            <Button 
              onClick={handleInviteMember}
              disabled={!inviteEmail || isInviting}
              className="savecation-button w-full"
              data-testid="send-invite-btn"
            >
              {isInviting ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Sending...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Mail className="w-4 h-4" /> Send Invitation
                </span>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Members Modal */}
      <Dialog open={showMembersModal} onOpenChange={setShowMembersModal}>
        <DialogContent className="sm:max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Users className="w-5 h-5 text-sky-500" /> {selectedGoal?.title} Members
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {goalMembers.map(member => (
              <div 
                key={member.user_id} 
                className="flex items-center gap-3 p-3 bg-slate-50 rounded-2xl"
              >
                <Avatar className="w-12 h-12">
                  <AvatarImage src={member.picture} />
                  <AvatarFallback className="bg-sky-100 text-sky-600">
                    {member.name?.charAt(0) || "?"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-slate-900">{member.name}</p>
                    {member.is_owner && (
                      <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                        <Crown className="w-3 h-3" /> Owner
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-500">
                    Contributed: <span className="font-semibold text-sky-600">${member.contributed?.toFixed(2) || "0.00"}</span>
                  </p>
                </div>
                {member.contributed > 0 && (
                  <Award className="w-5 h-5 text-amber-500" title="Top contributor" />
                )}
              </div>
            ))}
            
            {/* Leave Group Button (for non-owners) */}
            {selectedGoal?.user_id !== user?.user_id && (
              <Button
                variant="outline"
                onClick={() => handleLeaveGoal(selectedGoal?.goal_id)}
                className="w-full rounded-full text-rose-600 border-rose-200 hover:bg-rose-50"
                data-testid="leave-group-btn"
              >
                <LogOut className="w-4 h-4 mr-2" /> Leave Group Goal
              </Button>
            )}
            
            {/* Invite More Button (for owners) */}
            {selectedGoal?.user_id === user?.user_id && (
              <Button
                onClick={() => { setShowMembersModal(false); setShowInviteModal(true); }}
                className="savecation-button w-full"
                data-testid="invite-more-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" /> Invite More Members
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Edit Goal Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-sky-500" /> Edit Goal
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Goal Name</label>
              <Input
                value={editGoal.title}
                onChange={(e) => setEditGoal({ ...editGoal, title: e.target.value })}
                className="savecation-input"
                data-testid="edit-goal-title"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Destination (optional)</label>
              <Input
                value={editGoal.destination}
                onChange={(e) => setEditGoal({ ...editGoal, destination: e.target.value })}
                className="savecation-input"
                placeholder="e.g., Paris, France"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Target Amount</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                <Input
                  type="number"
                  value={editGoal.target_amount}
                  onChange={(e) => setEditGoal({ ...editGoal, target_amount: e.target.value })}
                  className="savecation-input pl-8"
                  data-testid="edit-goal-amount"
                />
              </div>
            </div>
            <Button onClick={handleEditGoal} className="savecation-button w-full" data-testid="submit-edit-goal">
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Goal Completion Celebration Modal */}
      <Dialog open={showCelebration} onOpenChange={setShowCelebration}>
        <DialogContent className="sm:max-w-sm rounded-3xl text-center">
          <div className="py-6">
            <div className="text-6xl mb-4">🎉</div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Goal Reached!</h2>
            <p className="text-slate-600 mb-2">
              You saved <span className="font-bold text-emerald-600">${celebrationGoal?.target_amount?.toFixed(0)}</span> for
            </p>
            <p className="text-lg font-bold text-slate-900 mb-6">{celebrationGoal?.title}</p>
            <div className="flex flex-col gap-3">
              <Button
                className="savecation-button w-full"
                onClick={() => { setShowCelebration(false); window.open("https://www.google.com/flights", "_blank", "noopener,noreferrer"); }}
              >
                <span>✈️ Book Your Trip Now</span>
              </Button>
              <Button
                variant="outline"
                className="w-full rounded-full"
                onClick={() => setShowCelebration(false)}
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog(d => ({ ...d, open: false }))}>
        <DialogContent className="sm:max-w-sm rounded-3xl">
          <DialogHeader>
            <DialogTitle>{confirmDialog.title}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 mt-1">{confirmDialog.message}</p>
          <div className="flex gap-3 mt-4">
            <Button
              variant="outline"
              className="flex-1 rounded-full"
              onClick={() => setConfirmDialog(d => ({ ...d, open: false }))}
            >
              Cancel
            </Button>
            <Button
              className="flex-1 bg-red-500 hover:bg-red-600 rounded-full"
              onClick={() => {
                setConfirmDialog(d => ({ ...d, open: false }));
                confirmDialog.onConfirm?.();
              }}
            >
              Confirm
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <BottomNav />
    </div>
  );
}
